/**
 * Training Curriculum - Table of Contents
 * Public training curriculum overview for the heat pump learning journey.
 * @module TrainingCurriculumIntro
 */

import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Dropdown, Modal, Space, Spin, Typography, message } from 'antd';
import {
  CheckSquareOutlined,
  DownOutlined,
  ExperimentOutlined,
  FormOutlined,
  GlobalOutlined,
  ReadOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import styles from './Intro.module.css';
import LIFELogo from '../../Home/LIFE_logo.png';
import PagesWrapper from '../../../PagesWrapper';
import countriesService from '../../../services/countriesService';
import trainingCurriculumService from '../../../services/trainingCurriculumService';
import {
  normalizeLanguageCode,
  setGuestLanguagePreference,
} from '../../../utils/languagePreference';
import { useAuth } from '../../../context/AuthContext';
import {
  getCourseStructureByType,
  TRAINING_CURRICULUM_TYPES,
  trainingCurriculumTypeOrder,
} from './courseStructure';

const { Title, Paragraph, Text } = Typography;

const getSiteLanguageCode = (i18n) => normalizeLanguageCode(i18n.resolvedLanguage || i18n.language) || 'en';
const getTreeNodeKey = (curriculumType, nodeKey) => {
  const pathPrefix = curriculumType === TRAINING_CURRICULUM_TYPES.INSTALLER
    ? 'trainingCurriculum.tree.nodes'
    : `trainingCurriculum.treeVariants.${curriculumType}.nodes`;

  return `${pathPrefix}.n${nodeKey.replace(/\./g, '_')}`;
};

const inferCountryLanguageCode = (country) => {
  const value = `${country?.language || ''} ${country?.name || ''}`.trim().toLowerCase();

  if (!value) return null;
  if (/\b(en|english|englisch|anglais|engels)\b/.test(value)) return 'en';
  if (/\b(de|deutsch|german|allemand|duits)\b/.test(value)) return 'de';
  if (/\b(fr|français|francais|french|frans)\b/.test(value)) return 'fr';
  if (/\b(nl|nederlands|dutch|néerlandais|niederländisch)\b/.test(value)) return 'nl';

  return null;
};

const resolvePreferredCountry = (countryList, languageCode) => {
  return countryList.find((country) => inferCountryLanguageCode(country) === languageCode) || countryList[0] || null;
};

const buildAssignmentMap = (items = []) => Object.fromEntries(items.map((entry) => [entry.nodeKey, entry]));

const openResourceTarget = (target) => {
  if (typeof window !== 'undefined') {
    window.open(target, '_blank', 'noopener,noreferrer');
  }
};

const getLocalizedNode = (node, t, curriculumType) => ({
  ...node,
  title: t(`${getTreeNodeKey(curriculumType, node.nodeKey)}.title`, node.title),
  description: t(`${getTreeNodeKey(curriculumType, node.nodeKey)}.description`, node.description),
});

const buildResourceItems = (assignment, t) => {
  if (!assignment) {
    return [];
  }

  return [
    assignment.trainingProfile ? {
      key: 'trainingProfile',
      title: t('trainingCurriculum.resources.trainingProfile', 'Training Profile'),
      description: assignment.trainingProfile.name,
      icon: <ExperimentOutlined />,
      target: `/simulations/heat-pump-training?profileId=${assignment.trainingProfile.id}`,
    } : null,
    assignment.quizCollection ? {
      key: 'quizCollection',
      title: t('trainingCurriculum.resources.quizCollection', 'Quiz Collection'),
      description: assignment.quizCollection.name,
      icon: <CheckSquareOutlined />,
      target: `/quiz/${assignment.quizCollection.id}`,
    } : null,
    assignment.learningPath ? {
      key: 'learningPath',
      title: t('trainingCurriculum.resources.learningPath', 'Learning Path'),
      description: assignment.learningPath.name,
      icon: <ReadOutlined />,
      target: `/learning-path/${assignment.learningPath.id}`,
    } : null,
    assignment.survey ? {
      key: 'survey',
      title: t('trainingCurriculum.resources.survey', 'Survey'),
      description: assignment.survey.name,
      icon: <FormOutlined />,
      target: `/survey/${assignment.survey.id}`,
    } : null,
  ].filter(Boolean);
};

/**
 * Training Curriculum introduction component.
 * Renders the full curriculum structure with country/language-aware resource assignments.
 */
const TrainingCurriculumIntro = () => {
  const { t, i18n } = useTranslation();
  const { user, hasConfirmedSession } = useAuth();
  const [countries, setCountries] = useState([]);
  const [selectedCurriculumType, setSelectedCurriculumType] = useState(TRAINING_CURRICULUM_TYPES.INSTALLER);
  const [selectedCountryId, setSelectedCountryId] = useState(null);
  const [assignmentsByNode, setAssignmentsByNode] = useState({});
  const [loading, setLoading] = useState(true);
  const [assignmentsLoading, setAssignmentsLoading] = useState(false);
  const [pageError, setPageError] = useState('');
  const [activeNode, setActiveNode] = useState(null);

  const selectedCountry = countries.find((entry) => entry.id === selectedCountryId) || null;
  const selectedCourseStructure = useMemo(
    () => getCourseStructureByType(selectedCurriculumType),
    [selectedCurriculumType]
  );
  const selectedCurriculumLabel = t(`trainingCurriculum.curriculumTypes.${selectedCurriculumType}`, 'Installer');
  const availableLanguageItems = useMemo(() => countries.map((country) => ({
    key: String(country.id),
    label: `${country.language}${country.name ? ` · ${country.name}` : ''}`,
  })), [countries]);
  const availableCurriculumTypeItems = useMemo(
    () => trainingCurriculumTypeOrder.map((curriculumType) => ({
      key: curriculumType,
      label: t(`trainingCurriculum.curriculumTypes.${curriculumType}`, curriculumType),
    })),
    [t]
  );
  const hasNodeAssignment = (nodeKey) => Boolean(assignmentsByNode[nodeKey]);

  const sectionHasAssignedChild = (section) => section.subsections.some((subsection) => hasNodeAssignment(subsection.nodeKey));
  const siteLanguageCode = getSiteLanguageCode(i18n);
  const sectionCount = selectedCourseStructure.length;
  const topicCount = selectedCourseStructure.reduce((count, section) => count + section.subsections.length, 0);

  useEffect(() => {
    const fetchCountries = async () => {
      setLoading(true);
      setPageError('');

      try {
        const countryList = await countriesService.getAllCountries({ suppressNotification: true });
        setCountries(countryList || []);

        if (!countryList?.length) {
          setAssignmentsByNode({});
          return;
        }

        const preferredCountry = resolvePreferredCountry(countryList, siteLanguageCode);
        setSelectedCountryId((currentCountryId) => {
          if (currentCountryId && countryList.some((country) => country.id === currentCountryId)) {
            return currentCountryId;
          }

          return preferredCountry?.id ?? null;
        });
      } catch (error) {
        console.error('Failed to load curriculum countries:', error);
        setPageError(t('trainingCurriculum.loadError', 'Failed to load the training curriculum.'));
        setCountries([]);
        setAssignmentsByNode({});
      } finally {
        setLoading(false);
      }
    };

    fetchCountries();
  }, [siteLanguageCode, t]);

  useEffect(() => {
    if (!countries.length) {
      return;
    }

    const currentCountry = countries.find((entry) => entry.id === selectedCountryId) || null;
    const currentCountryLanguageCode = inferCountryLanguageCode(currentCountry);
    if (currentCountryLanguageCode === siteLanguageCode) {
      return;
    }

    const preferredCountry = resolvePreferredCountry(countries, siteLanguageCode);
    if (preferredCountry && preferredCountry.id !== selectedCountryId) {
      setSelectedCountryId(preferredCountry.id);
    }
  }, [countries, selectedCountryId, siteLanguageCode]);

  useEffect(() => {
    const fetchAssignments = async () => {
      if (!selectedCountryId) {
        setAssignmentsByNode({});
        return;
      }

      setAssignmentsLoading(true);
      try {
        const response = await trainingCurriculumService.getPublicAssignments(selectedCountryId, selectedCurriculumType, { suppressNotification: true });
        setAssignmentsByNode(buildAssignmentMap(response.items || []));
      } catch (error) {
        console.error('Failed to load curriculum assignments:', error);
        setAssignmentsByNode({});
        message.error(t('trainingCurriculum.assignmentLoadError', 'Failed to load curriculum assignments.'));
      } finally {
        setAssignmentsLoading(false);
      }
    };

    fetchAssignments();
  }, [selectedCountryId, selectedCurriculumType, t]);

  useEffect(() => {
    setActiveNode(null);
  }, [selectedCountryId, selectedCurriculumType]);

  const handleOpenNode = (nodeKey, nodeTitle) => {
    const assignment = assignmentsByNode[nodeKey];
    const resources = buildResourceItems(assignment, t);

    if (!resources.length) {
      return;
    }

    if (resources.length === 1) {
      openResourceTarget(resources[0].target);
      return;
    }

    setActiveNode({ nodeKey, nodeTitle, resources });
  };

  const handleLanguageSelect = ({ key }) => {
    const countryId = Number(key);
    if (!countryId || countryId === selectedCountryId) {
      return;
    }

    const nextCountry = countries.find((entry) => entry.id === countryId) || null;
    const nextLanguageCode = inferCountryLanguageCode(nextCountry);

    setSelectedCountryId(countryId);

    if (nextLanguageCode && nextLanguageCode !== siteLanguageCode) {
      if (!user && !hasConfirmedSession) {
        setGuestLanguagePreference(nextLanguageCode);
      }

      i18n.changeLanguage(nextLanguageCode);
    }
  };

  const handleCurriculumTypeSelect = ({ key }) => {
    if (!key || key === selectedCurriculumType) {
      return;
    }

    setSelectedCurriculumType(key);
  };

  return (
    <PagesWrapper>
      <div className={styles.introContainer}>
        <header className={styles.header}>
          <Title level={1}>Training Curriculum</Title>
          <Paragraph className={styles.subtitle}>
            {t('trainingCurriculum.subtitle', 'Structured public learning curriculum for heat pump systems')}
          </Paragraph>
        </header>

        {pageError && (
          <Alert type="error" showIcon className={styles.infoAlert} message={pageError} />
        )}

        <section className={styles.introBlock}>
          <div className={styles.introContent}>
            <Text className={styles.introText}>
              {t('trainingCurriculum.intro', 'This curriculum provides a structured overview of the heat pump learning journey, covering foundational thermodynamic principles, system optimization topics, multi-room scenarios, and field-based case studies. Each section is designed to support guided learning paths, assessments, and profile-based practice.')}
            </Text>
          </div>
          <div className={styles.statsBar}>
            <div className={styles.stat}>
              <span className={styles.statNumber}>{sectionCount}</span>
              <span className={styles.statLabel}>{t('trainingCurriculum.mainSections', 'Main Sections')}</span>
            </div>
            <div className={styles.stat}>
              <span className={styles.statNumber}>{topicCount}</span>
              <span className={styles.statLabel}>{t('trainingCurriculum.topics', 'Topics')}</span>
            </div>
          </div>
        </section>

        {(loading || assignmentsLoading) && (
          <div className={styles.loading}><Spin size="large" /></div>
        )}

        {!loading && !countries.length && (
          <Alert
            type="info"
            showIcon
            className={styles.infoAlert}
            message={t('trainingCurriculum.noCountries', 'No country languages are available for the curriculum yet.')}
          />
        )}

        <nav className={styles.tableOfContents}>
          <div className={styles.tocHeader}>
            <Title level={3}>{t('trainingCurriculum.contents', 'Contents')}</Title>
            <Space size={10} wrap>
              <Dropdown
                menu={{
                  items: availableCurriculumTypeItems,
                  selectable: true,
                  selectedKeys: [selectedCurriculumType],
                  onClick: handleCurriculumTypeSelect,
                }}
                trigger={['click']}
                placement="bottomRight"
              >
                <button type="button" className={`${styles.languageDropdown} ${styles.languageDropdownCompact}`}>
                  <ReadOutlined />
                  <span>{`${t('trainingCurriculum.selectCurriculumType', 'Curriculum profile')}: ${selectedCurriculumLabel}`}</span>
                  <DownOutlined />
                </button>
              </Dropdown>

              {availableLanguageItems.length > 0 && (
                <Dropdown
                  menu={{
                    items: availableLanguageItems,
                    selectable: true,
                    selectedKeys: selectedCountryId ? [String(selectedCountryId)] : [],
                    onClick: handleLanguageSelect,
                  }}
                  trigger={['click']}
                  placement="bottomRight"
                >
                  <button type="button" className={`${styles.languageDropdown} ${styles.languageDropdownCompact}`}>
                    <GlobalOutlined />
                    <span>
                      {selectedCountry
                        ? `${t('trainingCurriculum.learningLanguage', 'Curriculum language')}: ${selectedCountry.language}`
                        : t('trainingCurriculum.selectCountryPlaceholder', 'Select a country and language')}
                    </span>
                    <DownOutlined />
                  </button>
                </Dropdown>
              )}
            </Space>
          </div>

          {selectedCourseStructure.map((rawSection) => {
            const section = getLocalizedNode(rawSection, t, selectedCurriculumType);
            const sectionDirectAssignment = hasNodeAssignment(section.nodeKey);
            const sectionChildAssignment = sectionHasAssignedChild(rawSection);
            const sectionMuted = !sectionDirectAssignment && !sectionChildAssignment;

            return (
            <article key={section.id} className={styles.section}>
              <button
                type="button"
                className={`${styles.sectionHeader} ${sectionDirectAssignment ? styles.sectionClickable : ''} ${!sectionDirectAssignment && sectionMuted ? styles.sectionMuted : ''} ${!sectionDirectAssignment && !sectionMuted ? styles.sectionNeutral : ''}`}
                onClick={() => sectionDirectAssignment && handleOpenNode(section.nodeKey, section.title)}
                onKeyDown={(event) => event.key === 'Enter' && sectionDirectAssignment && handleOpenNode(section.nodeKey, section.title)}
                aria-disabled={!sectionDirectAssignment}
              >
                <div className={styles.sectionIcon}>{section.icon || <ExperimentOutlined />}</div>
                <div className={styles.sectionMeta}>
                  <Title level={2} className={styles.sectionTitle}>
                    <span className={styles.sectionNumber}>{section.number}.</span>
                    {section.title}
                  </Title>
                  <Paragraph className={styles.sectionDescription}>
                    {section.description}
                  </Paragraph>
                </div>
              </button>

              <div className={styles.subsectionsList}>
                {rawSection.subsections.map((rawSub) => {
                  const sub = getLocalizedNode(rawSub, t, selectedCurriculumType);
                  const subAssigned = hasNodeAssignment(sub.nodeKey);

                  return (
                  <div
                    key={sub.id}
                    className={`${styles.subsectionItem} ${subAssigned ? '' : styles.subsectionDisabled}`}
                    onClick={() => handleOpenNode(sub.nodeKey, sub.title)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(event) => event.key === 'Enter' && handleOpenNode(sub.nodeKey, sub.title)}
                  >
                    <div className={styles.subsectionNumber}>{sub.id}</div>
                    <div className={styles.subsectionContent}>
                      <Text className={styles.subsectionTitle}>{sub.title}</Text>
                      <Text className={styles.subsectionDescription}>{sub.description}</Text>
                    </div>
                    <div className={styles.subsectionArrow}>{subAssigned ? '→' : '•'}</div>
                  </div>
                )})}
              </div>
            </article>
          )})}
        </nav>

        <footer className={styles.footer}>
          <div className={styles.footerDisclaimer}>
            <img
              src={LIFELogo}
              alt="Co-funded by the European Union"
              className={styles.footerDisclaimerImage}
            />
            <Text className={styles.footerDisclaimerText}>
              Co-funded by the European Union. Views and opinions expressed are however those of the
              author(s) only and do not necessarily reflect those of the European Union or CINEA.
              Neither the European Union nor the granting authority can be held responsible for them.
            </Text>
          </div>
        </footer>

        <Modal
          open={!!activeNode}
          footer={null}
          onCancel={() => setActiveNode(null)}
          title={activeNode?.nodeTitle || ''}
          wrapClassName={styles.assignmentModal}
        >
          <div className={styles.resourceList}>
            {activeNode?.resources.map((resource) => (
              <button
                key={resource.key}
                type="button"
                className={styles.resourceCard}
                onClick={() => {
                  openResourceTarget(resource.target);
                }}
              >
                <div className={styles.resourceIcon}>{resource.icon}</div>
                <div className={styles.resourceContent}>
                  <Text strong className={styles.resourceTitle}>{resource.title}</Text>
                  <Text className={styles.resourceDescription}>{resource.description}</Text>
                </div>
              </button>
            ))}
          </div>
          <Space className={styles.modalHint}>
            <Text type="secondary">{t('trainingCurriculum.modalHint', 'Choose one of the available resources for this curriculum node.')}</Text>
          </Space>
        </Modal>
      </div>
    </PagesWrapper>
  );
};

export default TrainingCurriculumIntro;
